import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import {
  collection,
  doc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore';
import { db, functions } from '../firebase';
import {
  REPORT_TYPES,
  QUARTERS,
  SEMESTERS,
  currentSchoolYear,
  schoolYearOptions,
  millisFromTimestamp,
  sortDraftsByGeneratedDesc,
  findMatchingDraft,
} from '../utils/narrativeDrafts';

/**
 * Teacher-facing modal that generates an AI report-card narrative draft via the
 * generateReportCardNarrative Cloud Function, then lets the teacher review,
 * edit, and save it. All PII filtering happens server-side; this component only
 * ever sends studentId + report options and receives the narrative text.
 *
 * Optional props let a caller (the Results "Saved Report Cards" overview) open
 * the modal pre-pointed at a period and auto-load a specific saved draft:
 *   initialReportType / initialQuarterNumber / initialSemester / initialSchoolYear
 *   autoOpenDraftId — when set and found among this student's drafts, that exact
 *                     draft is loaded into the editor on open. Passing the same
 *                     draft id the overview resolved guarantees View opens the
 *                     very draft whose status the overview displayed.
 */

function wordCount(text) {
  return text.trim() ? text.trim().split(/\s+/).length : 0;
}

function describeError(err) {
  const code = String(err?.code || '').toLowerCase();
  const message = err?.message || '';
  if (code.includes('resource-exhausted')) {
    return "You've reached today's draft limit (50 per day). Please try again tomorrow.";
  }
  if (code.includes('failed-precondition')) {
    // Server message is already teacher-friendly (e.g. too few assessments).
    return message || 'This report period does not have enough assessments yet.';
  }
  if (code.includes('permission-denied') || code.includes('unauthenticated')) {
    return 'You do not have permission to generate a narrative for this student.';
  }
  if (code.includes('not-found')) {
    return 'This student record could not be found.';
  }
  if (code.includes('invalid-argument')) {
    return message || 'Please check the report options and try again.';
  }
  return 'Something went wrong, please try again.';
}

const styles = {
  overlay: {
    position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
    zIndex: 1000, padding: '24px', overflowY: 'auto',
  },
  modal: {
    backgroundColor: '#fff', borderRadius: '10px', maxWidth: '680px', width: '100%',
    padding: '22px 24px', boxShadow: '0 10px 40px rgba(0,0,0,0.25)',
  },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' },
  title: { margin: 0, fontSize: '1.25em' },
  subtitle: { color: '#555', fontSize: '0.9em', marginBottom: '16px' },
  row: { display: 'flex', flexWrap: 'wrap', gap: '14px', marginBottom: '14px' },
  field: { display: 'flex', flexDirection: 'column', gap: '4px', flex: '1 1 180px' },
  label: { fontSize: '0.82em', fontWeight: 600, color: '#333' },
  select: { padding: '7px', borderRadius: '5px', border: '1px solid #bbb', fontSize: '0.95em' },
  textarea: { padding: '9px', borderRadius: '5px', border: '1px solid #bbb', fontSize: '0.95em', fontFamily: 'inherit', resize: 'vertical' },
  primaryBtn: { padding: '9px 18px', backgroundColor: '#3b82f6', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.95em' },
  secondaryBtn: { padding: '8px 14px', backgroundColor: '#e5e7eb', color: '#222', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.9em' },
  closeBtn: { background: 'none', border: 'none', fontSize: '1.4em', cursor: 'pointer', color: '#666', lineHeight: 1 },
  banner: { backgroundColor: '#fef3c7', border: '1px solid #f59e0b', borderRadius: '6px', padding: '9px 12px', fontSize: '0.85em', color: '#92400e', marginBottom: '10px' },
  errorBox: { backgroundColor: '#fee2e2', border: '1px solid #ef4444', borderRadius: '6px', padding: '9px 12px', fontSize: '0.88em', color: '#991b1b', marginBottom: '12px' },
  resumeBox: { backgroundColor: '#eff6ff', border: '1px solid #93c5fd', borderRadius: '6px', padding: '9px 12px', fontSize: '0.85em', color: '#1e40af', marginBottom: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px' },
  toolbar: { display: 'flex', gap: '10px', alignItems: 'center', marginTop: '10px', flexWrap: 'wrap' },
  meta: { color: '#666', fontSize: '0.82em' },
  ratingBar: { display: 'flex', alignItems: 'center', gap: '8px', margin: '6px 0 10px' },
  ratingPrompt: { fontSize: '0.8em', color: '#666' },
  ratingBtn: { padding: '2px 9px', fontSize: '1em', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer', lineHeight: 1.4 },
  ratingBtnActive: { background: '#ddd6fe', borderColor: '#8b5cf6' },
  ratingSaved: { color: '#16a34a', fontSize: '0.85em' },
  // Private teacher-flags section: distinct slate/amber styling so it can't be
  // mistaken for the parent-facing draft. Read-only, not in the textarea.
  flagsBox: { marginTop: '14px', backgroundColor: '#fffbeb', border: '1px solid #f59e0b', borderRadius: '8px', padding: '12px 14px' },
  flagsHeader: { fontWeight: 700, fontSize: '0.95em', color: '#92400e', marginBottom: '4px' },
  flagsCaption: { fontSize: '0.78em', color: '#92400e', marginBottom: '8px', fontStyle: 'italic' },
  flagsText: { fontSize: '0.92em', color: '#3f3f46', whiteSpace: 'pre-wrap', lineHeight: 1.45 },
};

function ReportCardNarrativeModal({
  user,
  student,
  onClose,
  initialReportType,
  initialQuarterNumber,
  initialSemester,
  initialSchoolYear,
  autoOpenDraftId,
}) {
  const studentId = student?.studentId || student?.id;

  const [reportType, setReportType] = useState(initialReportType || 'quarterly');
  const [quarterNumber, setQuarterNumber] = useState(initialQuarterNumber || 2);
  const [semester, setSemester] = useState(initialSemester || 'fall');
  const [schoolYear, setSchoolYear] = useState(initialSchoolYear || currentSchoolYear());
  const [teacherNotes, setTeacherNotes] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [narrative, setNarrative] = useState('');
  // Private teacher-only flags (from the dual-output Claude response). Read-only
  // in the UI, rendered in a separate boxed section below the editable
  // narrative. NEVER concatenated into `narrative`, NEVER included in Copy.
  const [teacherFlags, setTeacherFlags] = useState('');
  const [draftId, setDraftId] = useState(null);
  const [saveState, setSaveState] = useState('idle'); // idle | saving | saved | error
  const [copied, setCopied] = useState(false);

  // Teacher feedback on the draft (quality signal). All optional, client-written.
  const [rating, setRating] = useState(null); // 'positive' | 'negative' | null
  const [ratingSaved, setRatingSaved] = useState(false);
  const [editNotes, setEditNotes] = useState('');

  const [existingDrafts, setExistingDrafts] = useState([]);

  // Load existing drafts for this student so the teacher can resume.
  useEffect(() => {
    let cancelled = false;
    async function loadDrafts() {
      if (!studentId) return;
      try {
        const snap = await getDocs(collection(db, 'students', studentId, 'narrativeDrafts'));
        if (cancelled) return;
        const drafts = sortDraftsByGeneratedDesc(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setExistingDrafts(drafts);
      } catch (err) {
        console.error('ReportCardNarrativeModal: failed to load existing drafts', err);
      }
    }
    loadDrafts();
    return () => { cancelled = true; };
  }, [studentId]);

  // Most recent saved draft matching the currently-selected report period.
  // existingDrafts is already sorted newest-first, so findMatchingDraft returns
  // the most recent — the same draft the Results overview resolves to.
  const matchingDraft = useMemo(
    () => findMatchingDraft(existingDrafts, { reportType, schoolYear, quarterNumber, semester }),
    [existingDrafts, reportType, schoolYear, quarterNumber, semester],
  );

  const handleGenerate = useCallback(async () => {
    setError('');
    setSaveState('idle');
    // Fresh generation → reset any prior feedback / flags state.
    setRating(null);
    setRatingSaved(false);
    setEditNotes('');
    setTeacherFlags('');
    setLoading(true);
    try {
      const payload = { studentId, reportType, schoolYear };
      if (reportType === 'quarterly') payload.quarterNumber = quarterNumber;
      if (reportType === 'semiannual') payload.semester = semester;
      if (teacherNotes.trim()) payload.teacherNotes = teacherNotes.trim();

      const callable = httpsCallable(functions, 'generateReportCardNarrative');
      const res = await callable(payload);
      const data = res.data || {};
      const parentNarrative = data.parentNarrative || '';
      setNarrative(parentNarrative);
      setTeacherFlags(typeof data.teacherFlags === 'string' ? data.teacherFlags : '');
      setDraftId(data.draftId || null);

      // Freeze the AI baseline once for this freshly-created draft. The callable
      // creates the draft without aiDraftOriginal; this is the write-once set.
      // Rules reject any later change, so this is the only place it is written.
      // The baseline is the parent-facing text (teacherFlags is never copied here).
      if (data.draftId && parentNarrative) {
        try {
          await updateDoc(
            doc(db, 'students', studentId, 'narrativeDrafts', data.draftId),
            { aiDraftOriginal: parentNarrative },
          );
        } catch (baselineErr) {
          console.error('Failed to persist aiDraftOriginal baseline', baselineErr);
        }
      }
    } catch (err) {
      console.error('generateReportCardNarrative failed', err);
      setError(describeError(err));
    } finally {
      setLoading(false);
    }
  }, [studentId, reportType, schoolYear, quarterNumber, semester, teacherNotes]);

  const handleRate = useCallback(async (value) => {
    if (!draftId || !studentId) return;
    setRating(value);
    setRatingSaved(false);
    try {
      // Submit immediately on click — capture the gut reaction before editing.
      await updateDoc(doc(db, 'students', studentId, 'narrativeDrafts', draftId), {
        'feedback.rating': value,
        'feedback.ratedAt': serverTimestamp(),
      });
      setRatingSaved(true);
    } catch (err) {
      console.error('ReportCardNarrativeModal: rating save failed', err);
    }
  }, [draftId, studentId]);

  const handleSave = useCallback(async () => {
    if (!draftId || !studentId) return;
    setSaveState('saving');
    try {
      await setDoc(
        doc(db, 'students', studentId, 'narrativeDrafts', draftId),
        {
          narrative,
          finalEditedText: narrative,
          finalized: true,
          editedAt: serverTimestamp(),
          editedBy: user?.uid || null,
          // merge:true deep-merges the feedback map, preserving rating/ratedAt.
          ...(editNotes.trim() ? { feedback: { editNotes: editNotes.trim().slice(0, 1000) } } : {}),
        },
        { merge: true },
      );
      setSaveState('saved');
    } catch (err) {
      console.error('ReportCardNarrativeModal: save failed', err);
      setSaveState('error');
    }
  }, [draftId, studentId, narrative, editNotes, user]);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(narrative);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.error('clipboard write failed', err);
    }
  }, [narrative]);

  const handleDiscard = useCallback(() => {
    setNarrative('');
    setTeacherFlags('');
    setDraftId(null);
    setSaveState('idle');
    setRating(null);
    setRatingSaved(false);
    setEditNotes('');
    setError('');
  }, []);

  const loadDraftIntoEditor = useCallback(async (d) => {
    setNarrative(d.narrative || '');
    setDraftId(d.id);
    setSaveState(d.finalized ? 'saved' : 'idle');
    setRating(d.feedback?.rating || null);
    setRatingSaved(!!d.feedback?.rating);
    setEditNotes(d.feedback?.editNotes || '');
    setError('');
    // Best-effort: load private teacher flags for this draft (separate
    // subcollection). If the rule denies (e.g. teacher reassigned away) or none
    // exist, leave empty — never block the editor on this.
    setTeacherFlags('');
    try {
      const flagsSnap = await getDocs(
        query(
          collection(db, 'students', studentId, 'narrativeTeacherFlags'),
          where('draftId', '==', d.id),
        ),
      );
      if (!flagsSnap.empty) {
        // Most recent first (server timestamps are usually monotonic per draft,
        // but be defensive).
        const flagDocs = flagsSnap.docs
          .map((doc) => doc.data())
          .sort((a, b) => millisFromTimestamp(b.createdAt) - millisFromTimestamp(a.createdAt));
        setTeacherFlags(flagDocs[0]?.teacherFlagsText || '');
      }
    } catch (err) {
      console.error('Failed to load teacher flags for draft', err);
    }
  }, [studentId]);

  // When opened from the Saved Report Cards overview with an explicit draft id,
  // auto-load that exact draft once its data is available. Guarded by a ref so a
  // later re-render (or the teacher navigating away from the loaded draft) does
  // not re-open it.
  const autoOpenedRef = useRef(false);
  useEffect(() => {
    if (autoOpenedRef.current || !autoOpenDraftId || existingDrafts.length === 0) return;
    const target = existingDrafts.find((d) => d.id === autoOpenDraftId);
    if (target) {
      autoOpenedRef.current = true;
      loadDraftIntoEditor(target);
    }
  }, [autoOpenDraftId, existingDrafts, loadDraftIntoEditor]);

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <h2 style={styles.title}>📝 Report Card Narrative</h2>
          <button style={styles.closeBtn} onClick={onClose} aria-label="Close">×</button>
        </div>
        <div style={styles.subtitle}>{student?.name ? `For ${student.name}` : ''}</div>

        {/* Report options */}
        <div style={styles.row}>
          <div style={styles.field}>
            <label style={styles.label}>Report type</label>
            <select
              style={styles.select}
              value={reportType}
              onChange={(e) => setReportType(e.target.value)}
              disabled={loading}
            >
              {REPORT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>

          {reportType === 'quarterly' && (
            <div style={styles.field}>
              <label style={styles.label}>Quarter</label>
              <select
                style={styles.select}
                value={quarterNumber}
                onChange={(e) => setQuarterNumber(Number(e.target.value))}
                disabled={loading}
              >
                {QUARTERS.map((q) => <option key={q} value={q}>{`Q${q}`}</option>)}
              </select>
            </div>
          )}

          {reportType === 'semiannual' && (
            <div style={styles.field}>
              <label style={styles.label}>Semester</label>
              <select
                style={styles.select}
                value={semester}
                onChange={(e) => setSemester(e.target.value)}
                disabled={loading}
              >
                {SEMESTERS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
          )}

          <div style={styles.field}>
            <label style={styles.label}>School year</label>
            <select
              style={styles.select}
              value={schoolYear}
              onChange={(e) => setSchoolYear(e.target.value)}
              disabled={loading}
            >
              {schoolYearOptions().map((sy) => <option key={sy} value={sy}>{sy}</option>)}
            </select>
          </div>
        </div>

        <div style={{ ...styles.field, marginBottom: '14px' }}>
          <label style={styles.label}>
            Anything you'd like the AI to consider? Don't include any other student's name.
          </label>
          <textarea
            style={styles.textarea}
            rows={3}
            value={teacherNotes}
            onChange={(e) => setTeacherNotes(e.target.value)}
            disabled={loading}
            maxLength={4000}
            placeholder="Optional context for this child only…"
          />
        </div>

        {matchingDraft && !narrative && (
          <div style={styles.resumeBox}>
            <span>You have a saved draft for this period{matchingDraft.finalized ? ' (finalized)' : ''}.</span>
            <button style={styles.secondaryBtn} onClick={() => loadDraftIntoEditor(matchingDraft)}>
              Load it
            </button>
          </div>
        )}

        {error && <div style={styles.errorBox}>{error}</div>}

        {!narrative && (
          <button style={styles.primaryBtn} onClick={handleGenerate} disabled={loading}>
            {loading ? 'Generating…' : 'Generate Draft'}
          </button>
        )}
        {loading && <div style={styles.meta}>This usually takes 10–20 seconds.</div>}

        {/* Result editor */}
        {narrative && (
          <div>
            <div style={styles.banner}>
              ⚠️ AI-generated draft — please review and edit before sharing with parents.
            </div>
            <div style={styles.ratingBar}>
              <span style={styles.ratingPrompt}>Was this draft helpful as a starting point?</span>
              <button
                type="button"
                onClick={() => handleRate('positive')}
                style={{ ...styles.ratingBtn, ...(rating === 'positive' ? styles.ratingBtnActive : {}) }}
                aria-pressed={rating === 'positive'}
                title="Was this draft helpful as a starting point?"
              >👍</button>
              <button
                type="button"
                onClick={() => handleRate('negative')}
                style={{ ...styles.ratingBtn, ...(rating === 'negative' ? styles.ratingBtnActive : {}) }}
                aria-pressed={rating === 'negative'}
                title="Was this draft helpful as a starting point?"
              >👎</button>
              {ratingSaved && <span style={styles.ratingSaved}>✓ Thanks</span>}
            </div>
            <textarea
              style={{ ...styles.textarea, width: '100%' }}
              rows={14}
              value={narrative}
              onChange={(e) => { setNarrative(e.target.value); setSaveState('idle'); }}
            />
            {/* Private teacher-only flags. Read-only, distinct styling, never
                included in Copy or in the editable narrative. Only rendered
                when teacherFlags is a non-empty string. */}
            {teacherFlags && teacherFlags.trim().length > 0 && (
              <div style={styles.flagsBox}>
                <div style={styles.flagsHeader}>🔒 Private notes for you (not shared with parents)</div>
                <div style={styles.flagsCaption}>
                  These observations are visible only to you and admins. They are never shared with parents and are not part of the report card.
                </div>
                <div style={styles.flagsText}>{teacherFlags}</div>
              </div>
            )}
            <div style={{ ...styles.field, marginTop: '12px' }}>
              <label style={styles.label}>What did you change, and why? (optional, helps us improve the AI)</label>
              <textarea
                style={styles.textarea}
                rows={2}
                value={editNotes}
                onChange={(e) => setEditNotes(e.target.value)}
                maxLength={1000}
                placeholder="Don't include student names in your notes."
              />
              <span style={styles.meta}>{editNotes.length}/1000</span>
            </div>
            <div style={styles.toolbar}>
              <span style={styles.meta}>{wordCount(narrative)} words</span>
              <button style={styles.primaryBtn} onClick={handleSave} disabled={saveState === 'saving'}>
                {saveState === 'saving' ? 'Saving…' : saveState === 'saved' ? 'Saved ✓' : 'Save'}
              </button>
              <button style={styles.secondaryBtn} onClick={handleCopy}>
                {copied ? 'Copied ✓' : 'Copy'}
              </button>
              <button style={styles.secondaryBtn} onClick={handleDiscard}>Discard</button>
              <button style={styles.secondaryBtn} onClick={handleGenerate} disabled={loading}>
                Regenerate
              </button>
            </div>
            {saveState === 'error' && (
              <div style={{ ...styles.errorBox, marginTop: '10px' }}>
                Could not save. Please check your connection and try again.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default ReportCardNarrativeModal;
